import { styled } from '@mui/material'
import { PageArea, PageProps } from '..'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { VersionReportHeader } from './components/header'
import { VersionReportPage } from './components/pages/facade'
import { useCurrentVersion } from './hooks/currentReport'
import { useEvaluate } from './hooks/evaluate'
import { EvaluateResult } from '../../../domain/entity/version-report/evaluation'
import { useDiscussionPanel } from './hooks/useDiscussionPanel'
import { useVersionSelectMenu } from './hooks/useVersionSelectMenu'
import { DiscussionPanel } from './components/discussion-panel/index'
import { VersionSelectMenu } from './components/version-select-menu'
import { VersionConfigDialog } from './components/dialog/VersionConfigDialog'
import { Button } from '../../components/buttons'
import Loading, {
  useLoading,
} from '../../components/process-state-notifications/Loading'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import AlertDialog from '../../components/dialogs/AlertDialog'
import { doNotRequireSave, requireSave } from '../../../store/requiredSaveData'
import { useTemplateData } from './hooks/useTemplateData'
import { useReportData } from './hooks/useReportData'
import { useVersionReportVersionRepository } from '../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { intl } from '../../../i18n'
import { DiscussionProvider } from './context/DiscussionContext'
import { VersionReportVersionPageFormModel } from './components/dialog/VersionConfigDialog/model/VersionReportVersionPageFormModel'
import { colorPalette } from '../../style/colorPalette'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'

type ProjectVersionReportProps = PageProps
export const ProjectVersionReport = projectPrivate(
  ({ uuid }: ProjectVersionReportProps) => {
    const { projectUuid } = useProjectPrivateContext()
    const { isLoading, showLoading, hideLoading } = useLoading()
    const {
      currentPage,
      currentVersion,
      updateVersionUuid,
      updateCurrentVersion,
      storedVersionUuid,
      initialized,
      save,
      isDirty,
      changeLength,
      isSaving,
      discardDraft,
      refetchVersion,
      reCalculateAndFetchVersion,
      toNextPage,
      toPrevPage,
      updateRow,
      ...others
    } = useCurrentVersion(uuid, projectUuid)

    const rootRef = useRef<HTMLDivElement>(null)

    // ページ単位AI生成の結果を反映するAPI
    const gridApiRef = useRef<GridApi | null>(null)
    const handleGridReady = useCallback((e: GridReadyEvent) => {
      gridApiRef.current = e.api
    }, [])

    const { evaluatePage, evaluateRow } = useEvaluate(
      currentVersion,
      currentPage
    )

    const handleEvaluatePage = useMemo(() => {
      if (!evaluatePage) {
        return undefined
      }
      return async () => {
        showLoading()
        try {
          const results = await evaluatePage()
          const api = gridApiRef.current
          if (!api) return
          for (const result of results) {
            const node = api.getRowNode(result.uuid)
            if (!node) continue
            node.setDataValue('status', result.status)
            node.setDataValue('comment', result.comment)
          }
        } finally {
          hideLoading()
        }
      }
    }, [evaluatePage, showLoading, hideLoading])

    const handleEvaluateRow = useMemo(() => {
      if (!evaluateRow) {
        return undefined
      }
      return async (
        targetUuid: string
      ): Promise<EvaluateResult | undefined> => {
        showLoading()
        try {
          return await evaluateRow(targetUuid)
        } finally {
          hideLoading()
        }
      }
    }, [evaluateRow, showLoading, hideLoading])

    const discussionPanel = useDiscussionPanel()

    const [isVersionConfigDialogOpen, setIsVersionConfigDialogOpen] =
      useState<boolean>(false)
    const [previewPage, setPreviewPage] =
      useState<VersionReportVersionPageFormModel | null>(null)
    const [hasUnsavedConfigChanges, setHasUnsavedConfigChanges] =
      useState<boolean>(false)
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState<boolean>(false)
    const pendingCloseAction = useRef<(() => void) | null>(null)
    const { fetch: fetchVersion } = useVersionReportVersionRepository()

    const guardCloseVersionConfig = useCallback(
      (close: () => void) => {
        if (hasUnsavedConfigChanges) {
          pendingCloseAction.current = close
          setDiscardConfirmOpen(true)
          return
        }
        close()
      },
      [hasUnsavedConfigChanges]
    )

    const guardUnsavedEdit = useCallback(
      (action: () => void | Promise<void>): void | Promise<void> => {
        if (isDirty) {
          pendingCloseAction.current = () => {
            discardDraft()
            void action()
          }
          setDiscardConfirmOpen(true)
          return
        }
        return action()
      },
      [isDirty, discardDraft]
    )

    const guardedUpdateVersionUuid = useCallback(
      (v: string | undefined) => guardUnsavedEdit(() => updateVersionUuid(v)),
      [guardUnsavedEdit, updateVersionUuid]
    )

    const {
      isOpen: isVersionSelectMenuOpen,
      toggle: toggleVersionSelectMenu,
      open: openVersionSelectMenu,
      close: closeVersionSelectMenu,
      selectReport,
      handleAfterCreateReport,
    } = useVersionSelectMenu(guardedUpdateVersionUuid)

    const templateData = useTemplateData()
    const { selectTemplateByUuid, selectedTemplate } = templateData
    const reportData = useReportData(selectedTemplate?.uuid)

    const handleAfterDeleteVersion = useCallback(async () => {
      updateVersionUuid(undefined)
      await reportData.refreshReports()
      openVersionSelectMenu()
    }, [openVersionSelectMenu, updateVersionUuid, reportData.refreshReports])

    const openVersionConfig = useCallback(() => {
      closeVersionSelectMenu()
      discussionPanel.close()
      setIsVersionConfigDialogOpen(true)
    }, [closeVersionSelectMenu, discussionPanel])

    const guardedOpenVersionConfig = useCallback(() => {
      guardUnsavedEdit(() => openVersionConfig())
    }, [guardUnsavedEdit, openVersionConfig])

    const handleCloseVersionConfig = useCallback(() => {
      guardCloseVersionConfig(() => setIsVersionConfigDialogOpen(false))
    }, [guardCloseVersionConfig])

    const handleToggleVersionConfig = useCallback(() => {
      if (isVersionConfigDialogOpen) {
        guardCloseVersionConfig(() => setIsVersionConfigDialogOpen(false))
        return
      }
      guardedOpenVersionConfig()
    }, [
      isVersionConfigDialogOpen,
      guardCloseVersionConfig,
      guardedOpenVersionConfig,
    ])

    const handleAfterCreateReportWithInitial = useCallback(
      (uuid: string, isInitial: boolean) => {
        handleAfterCreateReport(uuid, isInitial)
        if (isInitial) {
          openVersionConfig()
        }
      },
      [handleAfterCreateReport, openVersionConfig]
    )

    const handleReportMenuToggle = useCallback(() => {
      if (!isVersionSelectMenuOpen && isVersionConfigDialogOpen) {
        guardCloseVersionConfig(() => {
          setIsVersionConfigDialogOpen(false)
          // guard content edits before opening the select menu,
          // since template/report edit inside the menu can overwrite the draft
          guardUnsavedEdit(() => toggleVersionSelectMenu())
        })
        return
      }
      if (!isVersionSelectMenuOpen) {
        guardUnsavedEdit(() => toggleVersionSelectMenu())
        return
      }
      toggleVersionSelectMenu()
    }, [
      isVersionSelectMenuOpen,
      isVersionConfigDialogOpen,
      guardCloseVersionConfig,
      guardUnsavedEdit,
      toggleVersionSelectMenu,
    ])

    const handleConfirmDiscard = useCallback(() => {
      setDiscardConfirmOpen(false)
      const action = pendingCloseAction.current
      pendingCloseAction.current = null
      action?.()
    }, [])

    const guardedRefetchVersion = useCallback(async () => {
      await guardUnsavedEdit(() => refetchVersion())
    }, [guardUnsavedEdit, refetchVersion])

    const guardedReCalculateAndFetchVersion = useCallback(async () => {
      await guardUnsavedEdit(() => reCalculateAndFetchVersion())
    }, [guardUnsavedEdit, reCalculateAndFetchVersion])

    const guardedToNextPage = useCallback(() => {
      guardUnsavedEdit(toNextPage)
    }, [guardUnsavedEdit, toNextPage])

    const guardedToPrevPage = useCallback(() => {
      guardUnsavedEdit(toPrevPage)
    }, [guardUnsavedEdit, toPrevPage])

    const handleCancelDiscard = useCallback(() => {
      setDiscardConfirmOpen(false)
      pendingCloseAction.current = null
    }, [])

    const handleAfterSubmitVersionConfig = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
      await reportData.refreshReports()
      setIsVersionConfigDialogOpen(false)
    }, [
      currentVersion,
      fetchVersion,
      updateCurrentVersion,
      reportData.refreshReports,
    ])

    const handleAfterUpdateTemplate = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
    }, [currentVersion, fetchVersion, updateCurrentVersion])

    const handleAfterUpdateReport = useCallback(async () => {
      if (!currentVersion) return
      const refreshed = await fetchVersion(currentVersion.uuid)
      if (refreshed) {
        updateCurrentVersion(refreshed)
      }
    }, [currentVersion, fetchVersion, updateCurrentVersion])

    useEffect(() => {
      if (initialized && !storedVersionUuid) {
        openVersionSelectMenu()
      }
    }, [initialized, storedVersionUuid, openVersionSelectMenu])

    useEffect(() => {
      if (currentVersion?.templateUuid) {
        selectTemplateByUuid(currentVersion.templateUuid)
      }
    }, [
      currentVersion?.templateUuid,
      isVersionSelectMenuOpen,
      selectTemplateByUuid,
    ])

    useEffect(() => {
      if (!isVersionConfigDialogOpen) {
        setPreviewPage(null)
      }
    }, [isVersionConfigDialogOpen])

    const dispatch = useDispatch()
    useEffect(() => {
      if (!isDirty) return
      dispatch(requireSave())
      return () => {
        dispatch(doNotRequireSave())
      }
    }, [isDirty, dispatch])

    useKeyBind(
      [
        {
          key: KEY_SAVE,
          fn: () => {
            if (isVersionConfigDialogOpen) return
            void save()
          },
          stopDefaultBehavior: true,
        },
      ],
      [save, isVersionConfigDialogOpen]
    )

    const isPreviewMode = isVersionConfigDialogOpen && previewPage !== null

    const effectivePage = isPreviewMode ? previewPage : currentPage

    if (!initialized) {
      return (
        <PageArea>
          <Loading isLoading={true} />
        </PageArea>
      )
    }

    return (
      <PageArea>
        <DiscussionProvider
          discussionPanel={discussionPanel}
          versionPageUuid={currentPage?.uuid}
          currentVersionUuid={currentVersion?.uuid}
          templatePageUuid={currentPage?.templatePageUuid}
          versionList={reportData.reports}
        >
          <Root ref={rootRef}>
            <VersionReportHeader
              currentPage={currentPage}
              currentVersion={currentVersion}
              {...others}
              toNextPage={guardedToNextPage}
              toPrevPage={guardedToPrevPage}
              refetchVersion={guardedRefetchVersion}
              reCalculateAndFetchVersion={guardedReCalculateAndFetchVersion}
              onEvaluatePage={handleEvaluatePage}
              updateRow={updateRow}
              discussionPanel={discussionPanel}
              isVersionSelectMenuOpen={isVersionSelectMenuOpen}
              onReportMenuToggle={handleReportMenuToggle}
              onAfterDeleteVersion={handleAfterDeleteVersion}
              isVersionConfigOpen={isVersionConfigDialogOpen}
              onToggleVersionConfig={handleToggleVersionConfig}
              showLoading={showLoading}
              hideLoading={hideLoading}
              guardUnsavedEdit={guardUnsavedEdit}
            />
            <MainContent>
              <VersionSelectMenu
                projectUuid={projectUuid}
                open={isVersionSelectMenuOpen}
                onClose={closeVersionSelectMenu}
                onReportSelect={selectReport}
                onAfterCreateReport={handleAfterCreateReportWithInitial}
                onAfterUpdateTemplate={handleAfterUpdateTemplate}
                onAfterUpdateReport={handleAfterUpdateReport}
                templateData={templateData}
                reportData={reportData}
              />
              {currentVersion && isVersionConfigDialogOpen && (
                <VersionConfigDialog
                  projectUuid={projectUuid}
                  open={isVersionConfigDialogOpen}
                  original={currentVersion}
                  initialSelectedPageNumber={currentPage?.pageNumber}
                  onAfterSubmit={handleAfterSubmitVersionConfig}
                  onClose={handleCloseVersionConfig}
                  onPreviewPageChange={setPreviewPage}
                  onUnsavedChangesChange={setHasUnsavedConfigChanges}
                />
              )}
              <PageContentArea
                isDiscussionOpen={discussionPanel.isOpen}
                isVersionConfigOpen={isVersionConfigDialogOpen}
              >
                {currentVersion ? (
                  <>
                    {isVersionConfigDialogOpen && (
                      <PreviewBadge aria-label="プレビュー表示中">
                        <PreviewDot />
                        プレビュー
                      </PreviewBadge>
                    )}
                    <VersionReportPage
                      snapshotUuid={currentVersion?.snapshotUuid}
                      page={effectivePage}
                      takenAt={currentVersion?.takenAt}
                      isPreview={isVersionConfigDialogOpen}
                      showLoading={showLoading}
                      hideLoading={hideLoading}
                      onEvaluateRow={handleEvaluateRow}
                      updateRow={updateRow}
                      onGridReady={handleGridReady}
                      {...others}
                    />
                  </>
                ) : (
                  <TemplateSelectionContainer>
                    <TemplateSelectionContent>
                      <TemplateSelectionButtonContainer>
                        <Button onClick={openVersionSelectMenu}>
                          {intl.formatMessage({
                            id: 'versionReport.selectReport',
                          })}
                        </Button>
                      </TemplateSelectionButtonContainer>
                    </TemplateSelectionContent>
                  </TemplateSelectionContainer>
                )}
              </PageContentArea>
              {currentPage && currentVersion && (
                <DiscussionPanel projectUuid={projectUuid} />
              )}
            </MainContent>
            <Loading isLoading={isLoading} elem={rootRef.current} />
            <SavePopper
              loading={isSaving}
              onSubmit={() => {
                void save()
              }}
              changeLength={changeLength}
            />
          </Root>
        </DiscussionProvider>
        <AlertDialog
          isOpen={discardConfirmOpen}
          title="編集中の内容があります"
          message="保存されていない変更があります。破棄してよろしいですか？"
          submitButtonTitle="破棄"
          closeButtonTitle="キャンセル"
          submitHandler={handleConfirmDiscard}
          closeHandler={handleCancelDiscard}
        />
      </PageArea>
    )
  }
)
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  height: '100%',
})

const MainContent = styled('div')({
  display: 'flex',
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
})

const PageContentArea = styled('div')<{
  isDiscussionOpen: boolean
  isVersionConfigOpen: boolean
}>(({ isDiscussionOpen, isVersionConfigOpen }) => ({
  position: 'relative',
  flex: 1,
  overflow: 'auto',
  // AG Grid 内部の z-index（ヘッダー・展開行・ag-tooltip 等）をこの stacking context に閉じ込め、
  // VersionSelectMenu がグリッドの上に正しく重なるようにする
  isolation: 'isolate',
  ...(isDiscussionOpen && {
    paddingRight: '16px',
  }),
  ...(isVersionConfigOpen && {
    paddingLeft: '16px',
  }),
  transition: 'all 0.3s ease',
}))

const PreviewBadge = styled('div')({
  position: 'absolute',
  top: '14px',
  right: '18px',
  zIndex: 30,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 10px',
  backgroundColor: colorPalette.yellow[0],
  border: `1px solid ${colorPalette.yellow[2]}`,
  borderRadius: '100px',
  color: colorPalette.monotone[10],
  fontSize: '11px',
  fontWeight: 600,
  pointerEvents: 'none',
  userSelect: 'none',
})

const PreviewDot = styled('span')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: colorPalette.yellow[5],
})

const TemplateSelectionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
})

const TemplateSelectionContent = styled('div')({
  textAlign: 'center',
  padding: '40px',
  maxWidth: '400px',
})

const TemplateSelectionButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
})
